import { FC } from "react";
import { twMerge } from "tailwind-merge";

import { getStudentIdFromAccessId, isStudentAccessId } from "../../../../shared/access-id";
import { PROFILE_PROPERTIES, getUserFullName, getUserInitials } from "../../../../utils/user-utils";

import { NewSubmissionBadge } from "components/materials/page/assessment/NewSubmissionBadge";
import { getSubmissionsByAccessId } from "components/materials/page/helpers";
import { ResponseType } from "components/materials/page/responses";
import { SectionUserType, SubmissionType } from "components/server-types";
import { t } from "i18n/i18n";
import { InitialsAvatar } from "mds/components/InitialsAvatar";
import { SquareButton, SquareButtonProps } from "mds/components/buttons/SquareButton";
import {
  PersonGroupIcon,
  PersonSingleIcon,
  ReactionTransparentVoteYesIcon,
  ReactionVoteYesIcon,
} from "mds/icons";
import { userSelectors } from "store/selectors";
import { useObjectSelector } from "store/store-hooks";

export const StudentUserAvatar = ({
  userId,
  isSelected,
  anonymize,
  className,
}: {
  userId: string;
  isSelected?: boolean;
  anonymize?: boolean;
  className?: string;
}) => {
  const user = useObjectSelector((s) => userSelectors.selectById(s, userId), PROFILE_PROPERTIES);

  if (!anonymize) {
    return (
      <InitialsAvatar
        className={className}
        initials={getUserInitials(user)}
        isSelected={isSelected}
      />
    );
  }

  return (
    <div
      className={twMerge(
        "flex size-3 items-center justify-center rounded-full p-3 text-[9px]",
        isSelected ? "text-white" : "text-blue",
        !isSelected && "bg-blue-tint-93",
        className,
      )}
    >
      <PersonSingleIcon />
    </div>
  );
};

const GroupAvatar = ({ isSelected }: { isSelected?: boolean }) => {
  return (
    <div
      className={twMerge(
        "flex size-3 items-center justify-center rounded-full p-3 text-[9px]",
        !isSelected && "bg-hibiscus-tint-95",
      )}
    >
      <PersonGroupIcon className={isSelected ? "text-white" : "text-hibiscus"} />
    </div>
  );
};

const AssessmentItemIcon = ({
  accessId,
  isSelected,
  isAssessedResponse,
  anonymize,
}: {
  accessId: string;
  isSelected?: boolean;
  isAssessedResponse?: boolean;
  anonymize?: boolean;
}) => {
  if (isAssessedResponse) {
    return (
      <div className="flex min-h-6 min-w-6 items-center justify-center">
        <ReactionVoteYesIcon />
      </div>
    );
  }

  if (isStudentAccessId(accessId)) {
    return (
      <StudentUserAvatar
        anonymize={anonymize}
        isSelected={isSelected}
        userId={getStudentIdFromAccessId(accessId)}
      />
    );
  }

  return <GroupAvatar isSelected={isSelected} />;
};

interface AssessmentStudentQueueItemProps {
  response: ResponseType;
  isSelected: boolean;
  anonymize: boolean;
  isAssessedResponse: boolean;
  isSaved: boolean;
  pageId: string | undefined;
  currentSubmissionId: string | undefined;
  sectionUsers: SectionUserType[];
  submissions: SubmissionType[];
  onResponseClick: (response: ResponseType) => void;
}

export const AssessmentStudentQueueItem: FC<AssessmentStudentQueueItemProps> = ({
  response,
  isSelected,
  anonymize,
  isAssessedResponse,
  isSaved,
  pageId,
  currentSubmissionId,
  sectionUsers,
  submissions,
  onResponseClick,
}) => {
  const studentUserId = isStudentAccessId(response.id)
    ? getStudentIdFromAccessId(response.id)
    : null;
  const user = useObjectSelector(
    (s) => (studentUserId ? userSelectors.selectById(s, studentUserId) : null),
    PROFILE_PROPERTIES,
  );

  const title = studentUserId && !anonymize && user ? getUserFullName(user) : response.title;

  const studentSubmissions = getSubmissionsByAccessId(response.id, sectionUsers, submissions);

  const submitBtnAttrs: Omit<SquareButtonProps, "kind" | "size"> = currentSubmissionId
    ? {
        form: "assessment-grading-form",
        type: "submit",
        name: "submitSource",
        value: `queue:${response.id}`,
      }
    : { onClick: () => onResponseClick(response) };

  return (
    <SquareButton
      aria-label={title}
      aria-selected={isSelected || undefined}
      className={`my-1 w-full justify-start ${isSaved ? "cursor-default border-green bg-green hover:border-green hover:bg-green" : ""}`}
      eventName="Assessment - Student Queue"
      eventProps={{
        pageId,
        userId: response.id,
        submissionId: currentSubmissionId,
      }}
      kind={isSelected ? "accent" : "ghost"}
      role="option"
      size="m"
      tabIndex={isSelected ? 0 : -1}
      title={title}
      {...submitBtnAttrs}
    >
      {isSaved ? (
        <span className="animate-save-fade flex items-center gap-2">
          <span className="flex min-h-6 min-w-6 items-center justify-center">
            <ReactionTransparentVoteYesIcon className="text-white" />
          </span>
          <div className="min-w-0 text-sm font-medium text-white">{t("assessment.saved")}</div>
        </span>
      ) : (
        <>
          <AssessmentItemIcon
            accessId={response.id}
            anonymize={anonymize}
            isAssessedResponse={isAssessedResponse}
            isSelected={isSelected}
          />
          <div
            className={twMerge(
              "min-w-0 truncate text-sm font-medium",
              isSelected ? "text-white" : "text-black-tint-20",
            )}
          >
            {title}
          </div>
          <NewSubmissionBadge sortedSubmissions={studentSubmissions} />
        </>
      )}
    </SquareButton>
  );
};
